import type {
  CatalogTemplate,
  GenerationResponse,
  GenerationHistoryResponse,
  TemplateCatalogResponse,
  WalletResponse,
  CreditPackagesResponse,
  PaymentMethod,
  PaymentResponse,
  ErrorResponse,
} from "@nura/contracts";

type Fetcher = (
  input: string | URL | Request,
  init?: RequestInit,
) => Promise<Response>;

export class CatalogApiError extends Error {
  constructor(public readonly status: number, public readonly code?: ErrorResponse["error"]) {
    super("catalog_request_failed");
    this.name = "CatalogApiError";
  }
}

function endpoint(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, "")}${path}`;
}

export function createCatalogApi(
  baseUrl: string,
  fetcher: Fetcher = globalThis.fetch,
  initData: string | (() => string) = "",
) {
  function authenticationHeaders(): Headers {
    const value = typeof initData === "function" ? initData() : initData;
    const headers = new Headers({ accept: "application/json" });
    if (value !== "") headers.set("authorization", `tma ${value}`);
    return headers;
  }
  async function generationResponse(response: Response): Promise<GenerationResponse> {
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as Partial<ErrorResponse>;
      throw new CatalogApiError(response.status, body.error);
    }
    return (await response.json()) as GenerationResponse;
  }

  return {
    async getWallet(): Promise<WalletResponse> {
      const response = await fetcher(endpoint(baseUrl, "/wallet"), {
        headers: authenticationHeaders(),
      });
      if (!response.ok) throw new CatalogApiError(response.status);
      return await response.json() as WalletResponse;
    },
    async getCreditPackages(): Promise<CreditPackagesResponse> {
      const response = await fetcher(endpoint(baseUrl, "/credit-packages"), {
        headers: authenticationHeaders(),
      });
      if (!response.ok) throw new CatalogApiError(response.status);
      return response.json() as Promise<CreditPackagesResponse>;
    },
    async createPayment(packageCode: string, method: PaymentMethod,
      idempotencyKey: string): Promise<PaymentResponse> {
      const headers = authenticationHeaders();
      headers.set("content-type", "application/json");
      const response = await fetcher(endpoint(baseUrl, "/payments"), {
        method: "POST", headers,
        body: JSON.stringify({ packageCode, method, idempotencyKey }),
      });
      if (!response.ok) throw new CatalogApiError(response.status);
      return response.json() as Promise<PaymentResponse>;
    },
    async getPayment(paymentId: string): Promise<PaymentResponse> {
      const response = await fetcher(endpoint(baseUrl,
        `/payments/${encodeURIComponent(paymentId)}`), { headers: authenticationHeaders() });
      if (!response.ok) throw new CatalogApiError(response.status);
      return response.json() as Promise<PaymentResponse>;
    },
    async cancelPayment(paymentId: string): Promise<PaymentResponse> {
      const response = await fetcher(endpoint(baseUrl,
        `/payments/${encodeURIComponent(paymentId)}/cancel`), {
        method: "POST", headers: authenticationHeaders(),
      });
      if (!response.ok) throw new CatalogApiError(response.status);
      return response.json() as Promise<PaymentResponse>;
    },
    async listGenerations(): Promise<GenerationResponse[]> {
      const response = await fetcher(endpoint(baseUrl, "/generations"), {
        headers: authenticationHeaders(),
      });
      if (!response.ok) throw new CatalogApiError(response.status);
      return (await response.json() as GenerationHistoryResponse).generations;
    },
    async readResult(resultUrl: string): Promise<Blob> {
      if (!/^\/api\/assets\/[a-zA-Z0-9_-]+\?variant=(?:preview|display|original)$/.test(resultUrl)) {
        throw new CatalogApiError(400);
      }
      const response = await fetcher(resultUrl, { headers: authenticationHeaders() });
      if (!response.ok) throw new CatalogApiError(response.status);
      return response.blob();
    },
    async listTemplates(): Promise<CatalogTemplate[]> {
      const response = await fetcher(endpoint(baseUrl, "/templates"), {
        headers: { accept: "application/json" },
      });
      if (!response.ok) {
        throw new CatalogApiError(response.status);
      }
      const body = (await response.json()) as TemplateCatalogResponse;
      if (!Array.isArray(body.templates)) {
        throw new CatalogApiError(response.status);
      }
      return body.templates;
    },

    async createGeneration(
      templateId: string,
      photo: File,
      idempotencyKey: string,
    ): Promise<GenerationResponse> {
      const form = new FormData();
      form.set("templateId", templateId);
      form.set("idempotencyKey", idempotencyKey);
      form.set("photo", photo);
      return generationResponse(
        await fetcher(endpoint(baseUrl, "/generations"), {
          method: "POST",
          headers: authenticationHeaders(),
          body: form,
        }),
      );
    },

    async getGeneration(generationId: string): Promise<GenerationResponse> {
      return generationResponse(
        await fetcher(
          endpoint(baseUrl, `/generations/${encodeURIComponent(generationId)}`),
          { headers: authenticationHeaders() },
        ),
      );
    },
  };
}

export type CatalogApi = ReturnType<typeof createCatalogApi>;
